import { createSelector, createSlice } from '@reduxjs/toolkit';
import { Constants } from '@pwc/pwc-deals-web-common';
import { clearAllProjectData } from '../actions/clearAllProjectData';
import resetCalculationDataForDocument from '../actions/resetCalculationDataForDocument';

export const initialState = {
  loadingInProcess: false,
  hasError: false,
  structuredDocuments: [],
};

const structuredAnalysisViewSlice = createSlice({
  name: 'structuredAnalysisViewSlice',
  initialState,
  reducers: {
    resetStructuredAnalysis: (_state, _action) => initialState,
    startProcessing: (_state, _action) => ({
      loadingInProcess: true,
      hasError: false,
      structuredDocuments: [],
    }),
    processingSucceeded: (state, action) => ({
      ...state,
      loadingInProcess: false,
      hasError: false,
      structuredDocuments: action.payload.documentsWithContent,
    }),
    processingFailed: (_state, _action) => ({
      loadingInProcess: false,
      hasError: true,
      structuredDocuments: [],
    }),

    startSharing: (_state, _action) => ({
      loadingInProcess: true,
      hasError: false,
    }),
    sharingSucceeded: () => ({
      loadingInProcess: false,
      hasError: false,
    }),
    sharingFailed: () => ({
      loadingInProcess: false,
      hasError: true,
    }),
  },

  extraReducers: (builder) => {
    builder.addCase(clearAllProjectData, () => initialState);
    builder.addCase(resetCalculationDataForDocument, (state, action) => {
      if (!action.payload || !action.payload.documentCode) {
        return state;
      }
      const { documentCode } = action.payload;
      const updatedDocuments = state.structuredDocuments
        ? state.structuredDocuments.filter((x) => x.code !== documentCode)
        : [];

      return { ...state, structuredDocuments: updatedDocuments };
    });
  },
});

export const { reducer: structuredAnalysisViewReducer } =
  structuredAnalysisViewSlice;

// Actions
export const {
  resetStructuredAnalysis,
  startProcessing,
  processingSucceeded,
  processingFailed,
  startSharing,
  sharingSucceeded,
  sharingFailed,
} = structuredAnalysisViewSlice.actions;

// Selectors
export const getStructuredAnalysisViewLoadingInProcess = (state) =>
  state.structuredAnalysisView.loadingInProcess;

export const getStructuredAnalysisViewHasError = (state) =>
  state.structuredAnalysisView.hasError;

export const getStructuredAnalysisViewDocumentsWithContent = (state) =>
  state.structuredAnalysisView.structuredDocuments;

export const getStructuredAnalysisViewDocumentsWithContentForDocument = (
  documentCode
) =>
  createSelector(
    getStructuredAnalysisViewDocumentsWithContent,
    (structuredDocuments) => {
      const documentsForCode = (structuredDocuments || []).find(
        (x) => x.code === documentCode
      );
      return documentsForCode;
    }
  );

export const selectDraftVersion = (documentCode) =>
  createSelector(
    getStructuredAnalysisViewDocumentsWithContentForDocument(documentCode),
    (documentForCode) =>
      documentForCode &&
      documentForCode.versions &&
      documentForCode.versions.length > 0 &&
      documentForCode.versions.find(
        (item) =>
          item.versionStatus !== Constants.VERSION_STATUS.SHARED.value &&
          item.versionStatus !==
            Constants.VERSION_STATUS.SHARED_WITH_THIRD_PARTY.value
      )
  );

export const selectSharedVersion = (documentCode) =>
  createSelector(
    getStructuredAnalysisViewDocumentsWithContentForDocument(documentCode),
    (documentForCode) =>
      documentForCode &&
      documentForCode.versions &&
      documentForCode.versions.length > 0 &&
      documentForCode.versions.find(
        (item) =>
          item.versionStatus === Constants.VERSION_STATUS.SHARED.value ||
          item.versionStatus ===
            Constants.VERSION_STATUS.SHARED_WITH_THIRD_PARTY.value
      )
  );
